import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ExportModal from '../Components/Export/ExportModal';
import ReportingService from '../AppSettings/services/ReportingService';
import './MattersPage.css';
import MattersTable from './Components/MattersTable/MattersTable';
import type { MatterRow } from './Components/MattersTable/MattersTable';
import ProjectsListView from './ProjectsListView';

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import MatterService from '../AppSettings/services/MatterService';
import ScheduleService from '../AppSettings/services/ScheduleService';
import type { ScheduleProjectSummary } from '@frontend/types/workflows';
import { isEngineeringEnvironment } from '../AppSettings/environmentConfig';
import useActiveEmployeeId from '@frontend/AppSettings/hooks/useActiveEmployeeId';
import useCurrentEmployee from '@frontend/AppSettings/hooks/useCurrentEmployee';
import Tabs from '@frontend/Components/Tabs';
import FilterDropdown from '@frontend/Components/FilterDropdown';
import SearchBar from '@frontend/Components/Inputs/SearchBar/SearchBar';
import CreateButton from '@frontend/Components/CreateButton/CreateButton';
import PageHeader from '@frontend/Components/Titles/PageHeader/PageHeader';
import { subDays, startOfDay, differenceInHours } from 'date-fns';
import {
  ARCHIVED_STATUS_FILTER_VALUE,
  isArchivedMatter,
} from './utils/matterArchiveStatus';

const EXPORT_MODAL_ID = 'matters-export';

type WorkloadStatus = 'completed' | 'at-risk' | 'in-progress' | null;

interface FilterOptionItem {
  value: string;
  label: string;
}

interface FilterOptions {
  areas: FilterOptionItem[];
  statuses: FilterOptionItem[];
  clients: FilterOptionItem[];
  leaders: FilterOptionItem[];
}

interface ExportModalComponentProps {
  isOpen: boolean;
  onClose: () => void;
}

interface MattersQueryResult {
  items: MatterRow[];
  totalItems: number;
  serverPaginated: boolean;
}

function normalizeToMatterRow(m: Record<string, unknown>): MatterRow {
  return {
    matterid: String(m.matterid ?? ''),
    mattername: m.mattername != null ? String(m.mattername) : undefined,
    clientname: m.clientname != null ? String(m.clientname) : undefined,
    employeename: m.employeename != null ? String(m.employeename) : undefined,
    status: m.status != null ? String(m.status) : undefined,
    mattertype: m.mattertype != null ? String(m.mattertype) : undefined,
    opendate: m.opendate != null ? String(m.opendate) : undefined,
    closedate: m.closedate != null ? String(m.closedate) : undefined,
    amount_spent: m.amount_spent != null ? (m.amount_spent as number | string) : undefined,
    budget: m.budget != null ? (m.budget as number | string) : undefined,
    amount: m.amount != null ? (m.amount as number | string) : undefined,
    progress: typeof m.progress === 'number' ? m.progress : undefined,
    milestoneCount: m.milestoneCount != null ? (m.milestoneCount as number | string) : undefined,
    completedCount: m.completedCount != null ? (m.completedCount as number | string) : undefined,
    application_no: m.application_no != null ? String(m.application_no) : undefined,
  };
}

function mapAggregateToMatterRow(project: ScheduleProjectSummary): MatterRow {
  return {
    matterid: project.projectId,
    mattername: project.projectName,
    clientname: project.clientName ?? '',
    employeename: project.ownerName ?? '',
    status: project.projectStatus ?? '',
    mattertype: project.projectType ?? '',
    opendate: project.firstPlannedDate ?? undefined,
    closedate: project.lastPlannedDate ?? undefined,
    progress: project.progress ?? 0,
    milestoneCount: project.milestoneCount ?? 0,
    completedCount: project.completedCount ?? 0,
    amount_spent: undefined,
    budget: undefined,
    amount: undefined,
  };
}

const LegalMattersPage = (): JSX.Element => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const employeeId = useActiveEmployeeId();
  const currentEmployee = useCurrentEmployee();
  const permishId = currentEmployee?.permish_id ?? null;
  const permishIdNum = typeof permishId === 'string' ? parseInt(permishId, 10) : permishId;
  const isAdmin = permishIdNum === 1 || permishIdNum === 2;

  const workloadFilterFromUrl = searchParams.get('workloadFilter');
  const viewFromUrl = searchParams.get('view');

  const [filteredMatters, setFilteredMatters] = useState<MatterRow[]>([]);
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(1);
  const [pageSize, setPageSize] = useState(7);
  const [showMyMatters, setShowMyMatters] = useState(viewFromUrl !== 'team');
  const [statusFilter, setStatusFilter] = useState<string>('Open');
  const [workloadFilter, setWorkloadFilter] = useState<string | null>(workloadFilterFromUrl || null);

  const [areaFilter, setAreaFilter] = useState('all');
  const [statusDropdownFilter, setStatusDropdownFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [leaderFilter, setLeaderFilter] = useState('all');
  const [openDateFilter, setOpenDateFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (viewFromUrl === 'team' && isAdmin) {
      setShowMyMatters(false);
    }
    if (workloadFilterFromUrl) {
      setWorkloadFilter(workloadFilterFromUrl);
    }
  }, [viewFromUrl, workloadFilterFromUrl, isAdmin]);

  useEffect(() => {
    if (!isAdmin && !showMyMatters) {
      setShowMyMatters(true);
    }
  }, [isAdmin, showMyMatters]);

  const calculateOptimalPageSize = useCallback((count: number): number => {
    if (count === 0) return 7;
    const pageSizeOptions = [5, 7, 10, 20, 50, 100];
    for (const option of pageSizeOptions) {
      if (option >= count) return option;
    }
    return 100;
  }, []);

  const [lastAutoAdjustedFilter, setLastAutoAdjustedFilter] = useState<string | null>(null);

  useEffect(() => {
    if (workloadFilter && lastAutoAdjustedFilter !== workloadFilter && filteredMatters.length > 0) {
      const optimalPageSize = calculateOptimalPageSize(filteredMatters.length);
      setPageSize(optimalPageSize);
      setPage(1);
      setLastAutoAdjustedFilter(workloadFilter);
    }
  }, [workloadFilter, filteredMatters.length, lastAutoAdjustedFilter, calculateOptimalPageSize]);

  useEffect(() => {
    if (!workloadFilter) {
      setLastAutoAdjustedFilter(null);
    }
  }, [workloadFilter]);

  const serverSearch = searchTerm || undefined;
  const serverStatus = statusDropdownFilter !== 'all' && statusDropdownFilter !== ARCHIVED_STATUS_FILTER_VALUE
    ? statusDropdownFilter : undefined;
  const serverClient = clientFilter !== 'all' ? clientFilter : undefined;
  const serverLeader = leaderFilter !== 'all' ? leaderFilter : undefined;

  const queryKey = isEngineeringEnvironment
    ? ['Matters', showMyMatters, employeeId, true, page, pageSize]
    : ['Matters', showMyMatters, employeeId, true, page, pageSize,
       serverSearch, serverStatus, serverClient, serverLeader];

  const { data, isPending, error, refetch } = useQuery<MattersQueryResult>({
    queryKey,
    queryFn: async (): Promise<MattersQueryResult> => {
      if (isEngineeringEnvironment) {
        const response = await ScheduleService.getAggregates({
          filters: showMyMatters && employeeId
            ? { userIds: [employeeId] }
            : {},
          pagination: {
            page,
            limit: pageSize,
            sortBy: 'projectName',
            sortOrder: 'asc',
          },
        });

        const aggregates = response?.data?.aggregates ?? [];
        const serverTotal = response?.data?.pagination?.total ?? aggregates.length;

        return {
          items: aggregates.map(mapAggregateToMatterRow),
          totalItems: serverTotal,
          serverPaginated: true,
        };
      }

      if (showMyMatters && employeeId) {
        const result = await MatterService.getMattersByEmployeePaginated(employeeId, {
          page,
          limit: pageSize,
          search: serverSearch,
          status: serverStatus,
        });
        const items = (result.items ?? []).map((item) =>
          normalizeToMatterRow(item as unknown as Record<string, unknown>)
        );
        return { items, totalItems: result.total, serverPaginated: true };
      }

      const result = await MatterService.getMattersPaginated({
        page,
        limit: pageSize,
        search: serverSearch,
        status: serverStatus,
        client: serverClient,
        leader: serverLeader,
      });
      const items = (result.items ?? []).map((item) =>
        normalizeToMatterRow(item as unknown as Record<string, unknown>)
      );
      return { items, totalItems: result.total, serverPaginated: true };
    },
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
  });

  const getWorkloadStatus = useCallback((matter: MatterRow): WorkloadStatus => {
    const now = new Date();
    const ninetyDaysAgo = subDays(startOfDay(now), 90);
    const status = (matter.status ?? '').trim().toLowerCase();
    const isCompleted = status === 'completed' || status === 'closed';

    if (isCompleted && matter.closedate) {
      try {
        const closeDate = startOfDay(new Date(matter.closedate));
        const nowDate = startOfDay(now);
        if (closeDate >= ninetyDaysAgo && closeDate <= nowDate) {
          return 'completed';
        }
      } catch {
        // invalid date
      }
    }

    if (!isCompleted || !matter.closedate) {
      if (matter.closedate) {
        try {
          const dueDate = new Date(matter.closedate);
          const hoursUntilDue = differenceInHours(dueDate, now);
          if (hoursUntilDue <= 24) return 'at-risk';
          return 'in-progress';
        } catch {
          return 'in-progress';
        }
      }
      return 'in-progress';
    }

    return null;
  }, []);

  const filterOptions = useMemo((): FilterOptions => {
    const source = data?.items;
    if (!source || source.length === 0) {
      return { areas: [], statuses: [], clients: [], leaders: [] };
    }

    const areas = new Set<string>();
    const statuses = new Set<string>();
    const clients = new Set<string>();
    const leaders = new Set<string>();

    source.forEach((matter) => {
      if (matter.status) statuses.add(matter.status);
      if (matter.clientname) clients.add(matter.clientname);
      if (matter.employeename) leaders.add(matter.employeename);
      if (matter.mattertype) areas.add(matter.mattertype);
    });

    return {
      areas: Array.from(areas).sort().map((area) => ({ value: area, label: area })),
      statuses: Array.from(statuses).sort().map((s) => ({ value: s, label: s })),
      clients: Array.from(clients).sort().map((c) => ({ value: c, label: c })),
      leaders: Array.from(leaders).sort().map((l) => ({ value: l, label: l })),
    };
  }, [data]);

  const archivedCount = useMemo(() => {
    if (!data?.items?.length) return 0;
    return data.items.filter((m) => isArchivedMatter(m)).length;
  }, [data]);

  const statusFilterOptions = useMemo(
    () => [
      { value: 'all', label: 'All Statuses' },
      {
        value: ARCHIVED_STATUS_FILTER_VALUE,
        label: (
          <span className="filter-option-archived">
            <span>Archived</span>
            <span
              className="matters-archived-count-badge matters-archived-count-badge--in-option"
              title={
                isEngineeringEnvironment
                  ? `${archivedCount} archived projects`
                  : `${archivedCount} archived matters`
              }
            >
              {archivedCount}
            </span>
          </span>
        ),
      },
      ...filterOptions.statuses,
    ],
    [archivedCount, filterOptions.statuses, isEngineeringEnvironment]
  );

  // Apply client-side filters. For server-paginated data (engineering) this
  // filters the current page only. For legal it filters the entire dataset.
  const applyClientFilters = useCallback(
    (items: MatterRow[]): MatterRow[] => {
      let result = [...items];

      // Sort by ID for consistent display
      result.sort((a, b) => {
        const idA = a.matterid ?? '';
        const idB = b.matterid ?? '';
        const partsA = idA.split('-');
        const partsB = idB.split('-');
        if (partsA.length > 1 && partsB.length > 1) {
          const numA = parseInt(partsA[partsA.length - 1], 10);
          const numB = parseInt(partsB[partsB.length - 1], 10);
          if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
        }
        return idA.localeCompare(idB);
      });

      if (statusFilter && statusFilter !== 'Open') {
        result = result.filter((m) => m.status === statusFilter);
      }

      if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        result = result.filter((m) => {
          const id = (m.matterid ?? '').toLowerCase();
          const name = (m.mattername ?? '').toLowerCase();
          return id.includes(lower) || name.includes(lower);
        });
      }

      if (areaFilter !== 'all') {
        result = result.filter((m) => m.mattertype === areaFilter);
      }
      if (statusDropdownFilter === 'all') {
        result = result.filter((m) => !isArchivedMatter(m));
      } else if (statusDropdownFilter === ARCHIVED_STATUS_FILTER_VALUE) {
        result = result.filter((m) => isArchivedMatter(m));
      } else {
        result = result.filter((m) => m.status === statusDropdownFilter);
      }
      if (clientFilter !== 'all') {
        result = result.filter((m) => m.clientname === clientFilter);
      }
      if (leaderFilter !== 'all') {
        result = result.filter((m) => m.employeename === leaderFilter);
      }
      if (workloadFilter) {
        result = result.filter((m) => getWorkloadStatus(m) === workloadFilter);
      }

      return result;
    },
    [statusFilter, searchTerm, areaFilter, statusDropdownFilter, clientFilter, leaderFilter, workloadFilter, getWorkloadStatus]
  );

  useEffect(() => {
    if (!data) return;

    if (data.serverPaginated) {
      // Server already filtered and paginated -- apply only client-only filters
      let result = data.items;

      if (statusDropdownFilter === ARCHIVED_STATUS_FILTER_VALUE) {
        result = result.filter((m) => isArchivedMatter(m));
      } else if (statusDropdownFilter === 'all') {
        result = result.filter((m) => !isArchivedMatter(m));
      }

      if (workloadFilter) {
        result = result.filter((m) => getWorkloadStatus(m) === workloadFilter);
      }

      setFilteredMatters(result);
      setPageLimit(Math.max(1, Math.ceil(data.totalItems / pageSize)));
    } else {
      const filtered = applyClientFilters(data.items);
      setFilteredMatters(filtered);
      setPageLimit(Math.max(1, Math.ceil(filtered.length / pageSize)));
    }
  }, [data, pageSize, applyClientFilters, statusDropdownFilter, workloadFilter, getWorkloadStatus]);

  // For server-paginated data the table receives the full (already paged) filtered list.
  // For client-paginated data we slice to the current page.
  const tableData = useMemo(() => {
    if (!data) return [];
    if (data.serverPaginated) return filteredMatters;
    return filteredMatters.slice((page - 1) * pageSize, page * pageSize);
  }, [data, filteredMatters, page, pageSize]);

  const totalItemsForPagination = useMemo(() => {
    if (!data) return 0;
    if (data.serverPaginated) return data.totalItems;
    return filteredMatters.length;
  }, [data, filteredMatters]);

  const handlePagination = (newPage: number): void => setPage(newPage);

  const handlePageSizeChange = (newPageSize: number): void => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const handleToggleMatters = useCallback(
    (showMy: boolean) => {
      setShowMyMatters(showMy);
      setPage(1);
    },
    []
  );

  const [reportType, setReportType] = useState<'all' | 'my'>('all');
  // 'all' hits POST /allmatters/excel, which is billing/admin only, so a non-admin must never
  // reach it. DERIVED rather than seeded into useState: permish arrives asynchronously
  // (useCurrentEmployee), and a useState initialiser runs once — seeding it would strand an
  // admin who mounted before their permish resolved on the personal report forever.
  const effectiveReportType: 'all' | 'my' = isAdmin ? reportType : 'my';

  const exportColumns = useMemo(
    () => [
      'Matter ID',
      'Matter Name',
      'Client ID',
      'Employee Name',
      'Matter Type',
      'Open Date',
      'Close Date',
      'Amount',
      'Status',
      'Description',
      'Urgency',
      'Country',
      'Case Type',
      'Application Date',
      'Application No',
      'Registration Date',
      'Registration No',
      'Publication Date',
      'Publication No',
      'Application Status',
      'Priority Data',
      'Office Action Date',
      'Expiry Date',
    ],
    []
  );

  const customMattersDataProcessor = useCallback(
    async (_data: unknown, selectedColumns: string[]): Promise<unknown[]> => {
      const req = { cols: selectedColumns };
      let response: Blob;

      if (effectiveReportType === 'all') {
        response = await ReportingService.getAllMattersExcel(req) as Blob;
      } else {
        if (!employeeId) {
          throw new Error('Cannot export personal matters without an employee id.');
        }
        response = await ReportingService.getMattersByEmployeeIdExcel(employeeId, req) as Blob;
      }

      const downloadUrl = window.URL.createObjectURL(response);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = effectiveReportType === 'all' ? 'MattersReport.xlsx' : 'MyMattersReport.xlsx';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 1000);

      return [];
    },
    [employeeId, effectiveReportType]
  );

  const exportModalComponent = useCallback(
    ({ isOpen, onClose }: ExportModalComponentProps) => (
      <ExportModal
        isOpen={isOpen}
        onClose={onClose}
        data={[]}
        columns={exportColumns}
        filename={effectiveReportType === 'all' ? 'MattersReport' : 'MyMattersReport'}
        title="Export Matters"
        customDataProcessor={customMattersDataProcessor}
        reportType={effectiveReportType}
        onReportTypeChange={setReportType}
        reportTypeOptions={[
          ...(isAdmin ? [{ value: 'all', label: 'All Matters' }] : []),
          { value: 'my', label: 'My Matters' },
        ]}
      />
    ),
    [customMattersDataProcessor, exportColumns, effectiveReportType, isAdmin]
  );

  const handleSearch = useCallback((term: string, _column?: string | null) => {
    setSearchTerm(term ?? '');
    setPage(1);
  }, []);

  const handleTabChange = useCallback((tabId: string) => {
    setShowMyMatters(tabId === 'my');
    setPage(1);
  }, []);


  if (isPending) return <span>Loading...</span>;
  if (error) return <span>Error: {error.message}</span>;

  return (
    <div className="mattersPage">
      <PageHeader
        title="Matters"
        subtitle="Manage and track all legal matters"
      >
        {isAdmin && (
          <CreateButton
            onClick={() => navigate('/createMatters')}
            text="New Matter"
            showIcon
            aria-label="Create new matter"
          />
        )}
      </PageHeader>

      <div className="matters-content-container">
        <div className="matters-tabs-search-section">
          <div className="matters-tabs-section">
            <Tabs
              tabs={[
                { id: 'my', label: 'My Matters' },
                ...(isAdmin ? [{ id: 'team', label: "Team's Matters" }] : []),
              ]}
              activeTabId={showMyMatters ? 'my' : 'team'}
              onTabChange={handleTabChange}
              variant="pills"
            />
            <span className="matters-count">
              Showing {tableData.length} of {totalItemsForPagination} matters
            </span>
          </div>
          <div className="matters-search-section">
            <SearchBar
              onSearch={handleSearch}
              placeholder="Search matters..."
              className="matters-search-bar"
            />
          </div>
        </div>

        <div className="matters-filters-row">
          <FilterDropdown
            label=""
            placeholder="All Clients"
            options={[
              { value: 'all', label: 'All Clients' },
              ...filterOptions.clients,
            ]}
            value={clientFilter}
            onChange={(value) => {
              setClientFilter(Array.isArray(value) ? value[0] : value);
              setPage(1);
            }}
            className="matters-filter-dropdown"
          />
          <FilterDropdown
            label=""
            placeholder="All Leaders"
            options={[
              { value: 'all', label: 'All Leaders' },
              ...filterOptions.leaders,
            ]}
            value={leaderFilter}
            onChange={(value) => {
              setLeaderFilter(Array.isArray(value) ? value[0] : value);
              setPage(1);
            }}
            className="matters-filter-dropdown"
          />
          <FilterDropdown
            label=""
            placeholder="All Statuses"
            options={statusFilterOptions}
            value={statusDropdownFilter}
            onChange={(value) => {
              setStatusDropdownFilter(Array.isArray(value) ? value[0] : value);
              setPage(1);
            }}
            className="matters-filter-dropdown"
          />
          <FilterDropdown
            label=""
            placeholder="All Health"
            options={[
              { value: 'all', label: 'All Health' },
              { value: 'healthy', label: 'Healthy' },
              { value: 'at-risk', label: 'At Risk' },
              { value: 'over-budget', label: 'Over Budget' },
            ]}
            value={areaFilter}
            onChange={(value) => {
              setAreaFilter(Array.isArray(value) ? value[0] : value);
              setPage(1);
            }}
            className="matters-filter-dropdown"
          />
          <FilterDropdown
            label=""
            placeholder="All Dates"
            options={[
              { value: 'all', label: 'All Dates' },
              { value: 'today', label: 'Today' },
              { value: 'week', label: 'This Week' },
              { value: 'month', label: 'This Month' },
              { value: 'year', label: 'This Year' },
            ]}
            value={openDateFilter}
            onChange={(value) => {
              setOpenDateFilter(Array.isArray(value) ? value[0] : value);
              setPage(1);
            }}
            className="matters-filter-dropdown"
          />
        </div>
      </div>

      <MattersTable
        data={tableData}
        showLeader={!showMyMatters}
        pageSize={pageSize}
        onMatterRefresh={() => refetch()}
        pagination={{
          currPage: page,
          onPageChange: handlePagination,
          pageLimit: pageLimit,
          pageSize: pageSize,
          onPageSizeChange: handlePageSizeChange,
          totalItems: totalItemsForPagination,
          itemLabel: 'matters',
        }}
      />
    </div>
  );
};

/**
 * Projects and matters are the same `matters` row surfaced under different labels. Engineering
 * renders the richer server-filtered Projects list (Schedule View tab, facet-driven dropdowns);
 * legal renders the paginated matters list above. The route (`/projects` vs `/matters`) and this
 * switch are the only place the two diverge at the page level — everything below (MattersTable,
 * MatterDetails) already branches on environment internally.
 */
const MattersPage = (): JSX.Element =>
  isEngineeringEnvironment ? <ProjectsListView /> : <LegalMattersPage />;

export default MattersPage;
